import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useLocation, useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
  styled,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import Iconify from '../../components/Iconify';
import useLocales from '../../hooks/useLocales';
import { useSelector, useDispatch } from '../../redux/store';
import conObj from '../../utils/connection-assistant';
import GameContainer from './GameContainer';
import { calculateFontSize } from '../../utils/formatString';
import useLoadOnScroll from './useLoadOnScroll';
import Back from '../../assets/home/back.png';
import AiwinImageBanner from '../../assets/aiwin_casino.jpg';
import Image from '../../components/Image';
import useResponsive from '../../hooks/useResponsive';

const NavigateContainer = styled(Box)(({ theme }) => ({
  cursor: 'pointer',
  display: 'flex',
  flexDirection: 'row',
  color: theme.palette.primary.main,
  paddingRight: theme.spacing(1),
  paddingLeft: theme.spacing(1),
}));

const SearchBox = styled(Box)(({ theme }) => ({
  position: 'relative',
  display: 'flex',
  borderRadius: 8,
  marginTop: 8,
  width: 'auto', // Allow width to adjust based on the content
  minWidth: '250px', // You can set a minimum width if you'd like
}));

const CategoryButton = styled(Button)(({ isActive }) => ({
  width: '180px',
  borderColor: '#ffd050',
}));

// const CategoryCard = styled(Card)(({ theme }) => ({
//   backgroundColor: '#2B2B2B',
//   marginBottom: 10,
//   paddingBottom: 10,
//   paddingTop: 10,
//   borderRadius: '10px !important',
//   marginRight: theme.spacing(1),
//   marginLeft: theme.spacing(1),
// }));

const SortButton = styled(Button)(({ theme, disabled }) => ({
  color: '#000',
  minWidth: 'unset',
  whiteSpace: 'nowrap',
  fontWeight: 300,
  borderRadius: 5,
  border: '1px solid #000',
  backgroundColor: '#fff',
  '&:disabled': {
    backgroundColor: '#B8860B',
    color: 'black',
  },
}));

const TypographyTitleStyled = styled(Typography)(({ theme, fontSize = 40, textAlign = 'center', mt = 6, mb = 4 }) => ({
  color: '#fead00',
  fontWeight: 'bold',
  textAlign,
  fontSize,

  [theme.breakpoints.down('md')]: {
    fontSize: fontSize * 0.8,
  },

  [theme.breakpoints.down('sm')]: {
    fontSize: fontSize * 0.6,
    marginTop: mt * 0.7,
    marginBottom: mb * 0.7,
  },

  [theme.breakpoints.down('xs')]: {
    fontSize: fontSize * 0.5,
  },

  marginTop: mt,
  marginBottom: mb,
}));

const GamesByCateg = ({ categSlug, companyId, searchValue, sortValue }) => {
  const isDesktop = useResponsive('up', 'lg');

  const params = useParams();

  const [searchParams] = useSearchParams();

  const id = params.id;

  const location = useLocation();

  const { gameCategsAndCompanies, isParent } = useSelector((x) => x.lookup);

  const { translate } = useLocales();

  const navigate = useNavigate();

  const [searchInput, setSearchInput] = useState('');

  const [selectedCateg, setSelectedCateg] = useState('');

  const [parentFilters, setParentFilters] = useState([]);

  const [filterIdx, setFilterIdx] = useState(0);

  const [categories, setCategories] = useState([]);

  const [providerName, setProviderName] = useState('All');

  // const [subCategs, setSubCategs] = useState([]);

  const {
    isLoading,
    error,
    companyName,
    gameList,
    setGameList,
    hasMore,
    handleSort,
    search,
    setSearch,
    sort,
    setSort,
    page,
    setPage,
  } = useLoadOnScroll(categSlug, companyId);

  useEffect(() => {
    try {
      if (location.state?.parentKey) {
        conObj.post(`games/game_categories.json?parent_key=${location.state?.parentKey}`).then((res) => {
          if (res.status === 200) {
            const tempCompanies = [];
            const companiesPerCateg = res.data?.data?.data;
            companiesPerCateg.forEach((item) => {
              tempCompanies.push(...item.companies);
            });

            // setSubCategs(tempCompanies);
          }
        });
      } else if (gameCategsAndCompanies?.length) {
        const searched = gameCategsAndCompanies.find((x) => x.slug === categSlug);
        if (searched) {
          setCategories(searched?.companies);
          setSelectedCateg(searched?.slug);
        }
      }
    } catch (err) {
      console.log(`Error: ${err?.message}`);
    }
  }, [categSlug, location.state?.parentKey, gameCategsAndCompanies]);

  // useEffect(() => {
  //   if (params?.companyId) {
  //     conObj.post(`games/game_categories.json`).then((response) => {
  //       const allData = response.data?.data?.data || [];

  //       const currentCategory = allData.find((cat) => cat.companies.some((comp) => comp.id === params.companyId));

  //       if (currentCategory) {
  //         setCategories(currentCategory.companies);

  //         setSelectedCateg(currentCategory.slug);
  //       }
  //     });
  //   }
  // }, [params?.companyId]);

  const onSearch = () => {
    setPage(0);
    if (searchInput === search) {
      setGameList([]);
    } else {
      setSearch(searchInput);
    }
  };

  useEffect(() => {
    if (!isParent) {
      const currentGames = gameCategsAndCompanies.filter((item) => item.slug === selectedCateg.slug);

      const filters = currentGames[0]?.companies?.map((item) => {
        return { id: item.id, sort: item.name, parentKey: item?.filter_parent_key, slug: item?.slug };
      });

      setParentFilters(filters);
    }
  }, [selectedCateg, gameCategsAndCompanies]);

  useEffect(() => {
    if (!isParent && parentFilters?.length) {
      const index = parentFilters?.findIndex((item) => {
        return item.id === companyId || location.state?.companyId;
      });

      setFilterIdx(index + 1);
    }
  }, [isParent, parentFilters, params, location.state]);

  useEffect(() => {
    setPage(0);
    setSearch(searchValue);
  }, [searchValue]);

  useEffect(() => {
    setPage(0);

    if (sortValue !== undefined) {
      handleSort(sortValue);
    }
  }, [sortValue]);

  const observer = useRef();

  const lastSlotElementRef = useCallback(
    (node) => {
      if (isLoading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setPage((prevState) => prevState + 1);
        }
      });
      if (node) observer.current.observe(node);
    },
    [isLoading, hasMore]
  );

  const handleCategClick = (item) => {
    setSelectedCateg(item?.category_slug);

    navigate(`/games/${item?.category_slug}/${item?.id}`, {
      replace: true,
      state: {
        companyId: item?.id,
        gameName: item?.name,
      },
    });
    // const _categSlug = item?.category_name?.toLowerCase()?.replace(' ', '-');
    // setProviderName(item?.category_name);
    // if (item?.is_parent && item?.filter_parent_key) {
    //   setPage((prevState) => 0);
    //   navigate(`/games/${_categSlug}`, {
    //     state: {
    //       parentKey: item?.filter_parent_key || location?.state?.parentKey,
    //       gameName: item?.name,
    //       ...(!item?.is_parent && { companyId: item?.id }),
    //     },
    //   });
    // } else {
    //   setPage((prevState) => 0);
    //   navigate(`/games/${_categSlug}/${item?.id}`, { state: {} });
    // }
  };

  useEffect(() => {
    setPage(0);
    setSearch('');
    setSort('');

    if (categSlug === 'popular') {
      setSort('popularity');
    }
  }, [params]);

  const resetSearch = () => {
    setSearch('');
    setSearchInput('');
    setPage(0);
    setGameList([]);
  };

  const renderGameList = () => {
    if (isLoading && gameList.length === 0) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
          <CircularProgress size={40} />
        </Box>
      );
    }

    if (gameList.length > 0) {
      return (
        <>
          {isDesktop && (
            <Box
              sx={{
                display: 'grid',
                gap: { xs: 1, md: 2 },
                gridTemplateColumns: {
                  xs: 'repeat(2, 1fr)',
                  sm: 'repeat(3, 1fr)',
                  md: 'repeat(4, 1fr)',
                  lg: 'repeat(5, 1fr)',
                  xl: 'repeat(6, 1fr)', // adjust if you want 7 like other layout
                },
                gridAutoFlow: 'dense',
              }}
            >
              {gameList.map((g, index) => {
                return (
                  <GameContainer
                    isParent
                    isCategorized
                    item={g}
                    titleSx={{
                      fontSize: {
                        xs: calculateFontSize(g?.name, 9, 10),
                        sm: calculateFontSize(g?.name, 10, 15),
                        md: calculateFontSize(g?.name, 11, 15),
                        lg: calculateFontSize(g?.name, 13, 17),
                        xl: calculateFontSize(g?.name, 13, 17),
                      },
                    }}
                  />
                );
              })}

              <Box ref={lastSlotElementRef} sx={{ gridColumn: 'span 2' }} />
            </Box>
          )}

          {!isDesktop && (
            <Box
              sx={{
                display: 'grid',
                gap: 1,
                gridTemplateColumns: 'repeat(3, 1fr)',
                gridAutoFlow: 'dense',
                mb: 3,
              }}
            >
              {gameList.map((g, index) => {
                return (
                  <Box key={g?.id}>
                    <GameContainer
                      isParent
                      isCategorized
                      item={g}
                      titleSx={{
                        fontSize: {
                          xs: calculateFontSize(g?.name, 8, 9),
                          sm: calculateFontSize(g?.name, 10, 15),
                          md: calculateFontSize(g?.name, 11, 15),
                          lg: calculateFontSize(g?.name, 13, 17),
                          xl: calculateFontSize(g?.name, 13, 17),
                        },
                      }}
                    />
                  </Box>
                );
              })}

              <Box ref={lastSlotElementRef} sx={{ gridColumn: 'span 2' }} />
            </Box>
          )}
        </>
      );
    }

    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
        <Typography variant="h6" color="textSecondary">
          {translate('no_games_available')}
        </Typography>
      </Box>
    );
  };

  return (
    <>
      <Box paddingBottom={{ xs: 10 }}>
        {/* {!categories || categories.length === 0 ? (
            <Box bgcolor="black" px={{ xs: 2, md: 20 }} py={4} textAlign="center">
              <Typography color="white">{translate('no_categories_available')}</Typography>
            </Box>
          ) : (
            <Box bgcolor="black" px={{ xs: 2, md: 20 }} py={2}>
              <Box sx={{ overflowX: 'auto' }} display="flex" gap={2} justifyContent="space-between">
                <Box display="flex" gap={2}>
                  {categories.map((item, i) => {
                    const isActive =
                      params?.companyId === item?.id ||
                      location.state?.companyId === item?.id ||
                      (item?.is_parent && location.state?.parentKey === item?.filter_parent_key);

                    return (
                      <CategoryButton
                        key={i}
                        disabled={item?.is_coming_soon}
                        variant="outlined"
                        isActive={isActive}
                        onClick={() => handleCategClick(item)}
                        sx={{
                          backgroundColor: isActive ? '#ffd050' : 'black',
                          color: isActive ? 'black' : 'white',
                          width: '180px',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            backgroundColor: isActive ? '#ffca2c' : '#ffd050',
                            color: 'black',
                            borderColor: '#ffd050',
                          },

                          '&.Mui-disabled': {
                            backgroundColor: '#222',
                            color: '#777',
                            borderColor: '#444',
                          },
                        }}
                      >
                        {item?.category_name?.toUpperCase()}
                      </CategoryButton>
                    );
                  })}
                </Box>
              </Box>
            </Box>
          )} */}

        <Box>
          {/* {!isParent && (
            <Box>
              <Box
                // onClick={() => navigate(`/arrangement/${params?.categSlug}`)}
                sx={{
                  mt: 3,
                  display: 'inline-flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                  width: 'fit-content',
                  gap: 1,
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    opacity: 0.7,
                    transform: 'translateX(-3px)',
                  },
                }}
              >
                <Image src={Back} sx={{ width: '20px', height: '20px' }} />
                <Typography
                  sx={{
                    fontWeight: 500,
                    fontSize: '16px',
                    color: '#FFD700', // Golden yellow
                  }}
                >
                  {translate('back_to_games')}
                </Typography>
              </Box>

              <SearchBox>
                <TextField
                  variant="outlined"
                  onChange={(e) => setSearchInput(e.target.value)}
                  value={searchInput}
                  fullWidth
                  placeholder={translate('search_games')}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        {searchInput && (
                          <IconButton
                            onClick={() => {
                              resetSearch();
                            }}
                          >
                            <Iconify icon="uil:times" />
                          </IconButton>
                        )}
                        <Iconify icon="material-symbols:search" sx={{ color: '#8b8383' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    mr: 1,
                    flexGrow: 1,

                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1,

                      '& fieldset': {
                        borderColor: '#000000',
                      },

                      '&:hover fieldset': {
                        borderColor: '#000000',
                      },

                      '&.Mui-focused fieldset': {
                        borderColor: '#000000',
                        borderWidth: 2,
                      },
                    },

                    input: {
                      color: '#000000',
                      '&::placeholder': {
                        color: '#8b8383',
                      },
                    },
                  }}
                />
                <LoadingButton
                  sx={{
                    color: '#000000',
                    border: '1px solid #000000',
                    flexShrink: 0,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      backgroundColor: '#8b8383',
                      color: '#030303',
                      border: '1px solid #8b8383',
                    },
                  }}
                  loading={isLoading}
                  disabled={isLoading}
                  variant="outlined"
                  onClick={onSearch}
                >
                  {translate('Search')}
                </LoadingButton>
              </SearchBox>
            </Box>
          )} */}
          {/* <Typography sx={{ textAlign: 'center' }}>{translate('providers')}</Typography> */}

          {/* {subCategs?.length ? (
              <Box sx={{ mb: 1 }}>
                {location?.state?.gameName ? (
                  <Typography sx={{ textAlign: 'center' }}>
                    {translate('x_category', { x: location.state.gameName })}
                  </Typography>
                ) : (
                  ''
                )}
                <Grid container spacing={'5px'} sx={{ padding: '5px' }}>
                  <Grid item xs={6} sm={4}>
                    <CategoryButton
                      variant="outlined"
                      isActive={!!(location?.state?.parentKey && !location.state?.companyId)}
                      onClick={() => {
                        if (location.state?.parentKey) {
                          setPage((prevState) => 0);
                          navigate(`/games/${params?.categSlug}/${params?.companyId}`, {
                            state: {
                              parentKey: location?.state?.parentKey,
                              gameName: location?.state?.gameName,
                            },
                          });
                        }
                      }}
                    >
                      {translate('All').toUpperCase()}
                    </CategoryButton>
                  </Grid>
                </Grid>
              </Box>
            ) : (
              <></>
            )} */}

          {/* {!isParent && (
            <Box>
              <Typography sx={{ color: '#FEAD00', mb: 1, mt: 1 }}>{translate('sort')}</Typography>
              <Stack spacing={1} direction="row" sx={{ mb: 2, overflowX: 'auto' }}>
                <SortButton
                  variant="outlined"
                  disabled={!sort}
                  onClick={() => {
                    setPage(0);
                    setGameList([]);
                    handleSort('');
                    // setCompanyId('all');
                    navigate(`/games/${categSlug}/${companyId}`, {
                      state: {
                        parentKey: location?.state?.parentKey || '',
                        companyId: location?.state?.companyId || '',
                        gameUrl: location?.state?.gameUrl || '',
                      },
                    });
                  }}
                >
                  {translate('all')}
                </SortButton>
                <SortButton
                  variant="outlined"
                  disabled={sort === 'popularity'}
                  onClick={() => handleSort('popularity')}
                >
                  {translate('Popular')}
                </SortButton>
                <SortButton variant="outlined" disabled={sort === 'a-z'} onClick={() => handleSort('a-z')}>
                  {translate('Name')}: A-Z
                </SortButton>
                <SortButton variant="outlined" disabled={sort === 'z-a'} onClick={() => handleSort('z-a')}>
                  {translate('Name')}: Z-A
                </SortButton>
              </Stack>
            </Box>
          )} */}

          <Box>{renderGameList()}</Box>

          {isLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', paddingBottom: 10 }}>
              <CircularProgress color="inherit" size={40} />
            </Box>
          )}
        </Box>
      </Box>
    </>
  );
};

GamesByCateg.propTypes = {};

export default GamesByCateg;
